import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  Shield, 
  Users, 
  TrendingUp, 
  Brain,
  Globe,
  CheckCircle
} from 'lucide-react';
import { useLogo } from '../../contexts/LogoContext';
import LanguageSwitcher from '../layout/LanguageSwitcher';
import './HomePage.css';

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const { logoPreview } = useLogo();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';

  // Default logo path if no custom logo
  const logoPath = logoPreview || '/logo192.png';

  // Handle My Account click - navigate to signin
  const handleMyAccountClick = (e) => {
    e.preventDefault();
    navigate('/signin');
  };

  return (
    <div className={`home-page ${isRTL ? 'rtl' : 'ltr'}`} style={{ minHeight: '100vh' }}>
      {/* Header */}
      <header className="home-header">
        <nav className="home-nav-container">
          <div className="home-logo-container">
            {logoPreview ? (
              <img src={logoPreview} alt="Matrix Logo" className="home-logo-img" />
            ) : (
              <div className="home-logo-text">Matrix</div>
            )}
          </div>
          <div className="home-nav-links">
            <a href="#features" className="home-nav-link">{t('home.features', 'Features')}</a>
            <a href="#benefits" className="home-nav-link">{t('home.benefits', 'Benefits')}</a>
            <button onClick={handleMyAccountClick} className="home-my-account-btn">
              {t('home.myAccount', 'My Account')}
            </button>
            <LanguageSwitcher variant="dropdown" />
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="home-hero">
        <div className="home-hero-content">
          <h1 className="home-hero-title">
            {t('home.heroTitle', 'Transform Your Organization with Matrix')}
          </h1>
          <p className="home-hero-subtitle">
            {t('home.heroSubtitle', 'Professional Assessment & Maturity Framework Platform for Enterprise Excellence')}
          </p>
          <div className="home-hero-cta">
            <button onClick={handleMyAccountClick} className="home-btn-primary">
              {t('home.getStarted', 'Get Started')}
            </button>
            <a href="#features" className="home-btn-secondary">
              {t('home.learnMore', 'Learn More')}
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="home-features" id="features">
        <div className="home-container">
          <h2 className="home-section-title">
            {t('home.featuresTitle', 'Powerful Features')}
          </h2>
          <p className="home-section-subtitle">
            {t('home.featuresSubtitle', 'Everything you need to assess, measure, and improve organizational maturity')}
          </p>
          
          <div className="home-features-grid">
            <div className="home-feature-card">
              <div className="home-feature-icon">
                <BarChart3 size={32} />
              </div>
              <h3>{t('home.feature1Title', 'Comprehensive Assessments')}</h3>
              <p>{t('home.feature1Desc', 'Create and manage detailed assessments with customizable frameworks tailored to your organization\'s needs and industry standards.')}</p>
            </div>
            
            <div className="home-feature-card">
              <div className="home-feature-icon">
                <TrendingUp size={32} />
              </div>
              <h3>{t('home.feature2Title', 'Maturity Reporting')}</h3>
              <p>{t('home.feature2Desc', 'Generate comprehensive maturity reports with AI-powered insights and actionable recommendations for continuous improvement.')}</p>
            </div>
            
            <div className="home-feature-card">
              <div className="home-feature-icon">
                <Users size={32} />
              </div>
              <h3>{t('home.feature3Title', 'Team Collaboration')}</h3>
              <p>{t('home.feature3Desc', 'Invite team members, assign roles, and collaborate seamlessly on assessments with real-time updates and notifications.')}</p>
            </div>
            
            <div className="home-feature-card">
              <div className="home-feature-icon">
                <Shield size={32} />
              </div>
              <h3>{t('home.feature4Title', 'Enterprise Security')}</h3>
              <p>{t('home.feature4Desc', 'Bank-level security with role-based access control, data encryption, and comprehensive audit trails for compliance.')}</p>
            </div>
            
            <div className="home-feature-card">
              <div className="home-feature-icon">
                <BarChart3 size={32} />
              </div>
              <h3>{t('home.feature5Title', 'Advanced Analytics')}</h3>
              <p>{t('home.feature5Desc', 'Track progress over time with detailed analytics, trend analysis, and performance dashboards for data-driven decisions.')}</p>
            </div>
            
            <div className="home-feature-card">
              <div className="home-feature-icon">
                <Brain size={32} />
              </div>
              <h3>{t('home.feature6Title', 'AI-Powered Insights')}</h3>
              <p>{t('home.feature6Desc', 'Leverage artificial intelligence to analyze assessments, generate recommendations, and identify improvement opportunities.')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="home-benefits" id="benefits">
        <div className="home-container">
          <h2 className="home-section-title">
            {t('home.benefitsTitle', 'Why Choose Matrix?')}
          </h2>
          <p className="home-section-subtitle">
            {t('home.benefitsSubtitle', 'Built for professionals who demand excellence')}
          </p>
          
          <div className="home-benefits-grid">
            <div className="home-benefit-item">
              <div className="home-benefit-icon">
                <CheckCircle size={24} />
              </div>
              <div className="home-benefit-content">
                <h4>{t('home.benefit1Title', 'Industry Standards')}</h4>
                <p>{t('home.benefit1Desc', 'Support for PMBOK, ISO, and custom frameworks')}</p>
              </div>
            </div>
            
            <div className="home-benefit-item">
              <div className="home-benefit-icon">
                <CheckCircle size={24} />
              </div>
              <div className="home-benefit-content">
                <h4>{t('home.benefit2Title', 'Scalable Solution')}</h4>
                <p>{t('home.benefit2Desc', 'From small teams to enterprise organizations')}</p>
              </div>
            </div>
            
            <div className="home-benefit-item">
              <div className="home-benefit-icon">
                <CheckCircle size={24} />
              </div>
              <div className="home-benefit-content">
                <h4>{t('home.benefit3Title', 'Multi-Language')}</h4>
                <p>{t('home.benefit3Desc', 'Full support for English and Arabic (RTL)')}</p>
              </div>
            </div>
            
            <div className="home-benefit-item">
              <div className="home-benefit-icon">
                <CheckCircle size={24} />
              </div>
              <div className="home-benefit-content">
                <h4>{t('home.benefit4Title', 'Cloud-Based')}</h4>
                <p>{t('home.benefit4Desc', 'Access your data anywhere, anytime')}</p>
              </div>
            </div>
            
            <div className="home-benefit-item">
              <div className="home-benefit-icon">
                <CheckCircle size={24} />
              </div>
              <div className="home-benefit-content">
                <h4>{t('home.benefit5Title', 'Expert Support')}</h4>
                <p>{t('home.benefit5Desc', 'Dedicated support team to help you succeed')}</p>
              </div>
            </div>
            
            <div className="home-benefit-item">
              <div className="home-benefit-icon">
                <CheckCircle size={24} />
              </div>
              <div className="home-benefit-content">
                <h4>{t('home.benefit6Title', 'Regular Updates')}</h4>
                <p>{t('home.benefit6Desc', 'Continuous improvements and new features')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="home-cta-section">
        <div className="home-container">
          <h2>{t('home.ctaTitle', 'Ready to Get Started?')}</h2>
          <p>{t('home.ctaSubtitle', 'Join organizations worldwide using Matrix to drive excellence')}</p>
          <button onClick={handleMyAccountClick} className="home-btn-primary home-btn-cta">
            {t('home.accessAccount', 'Access My Account')}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="home-container">
          <p>&copy; 2024 Matrix. {t('home.rightsReserved', 'All rights reserved.')} | {t('home.tagline', 'Professional Assessment & Maturity Framework Platform')}</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
